import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../lib/auth'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: Record<string, unknown>) => void
          renderButton: (el: HTMLElement, cfg: Record<string, unknown>) => void
          prompt: () => void
          disableAutoSelect: () => void
        }
      }
    }
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'

function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-gis]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Google sign-in could not load')))
      return
    }
    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.dataset.gis = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google sign-in could not load'))
    document.head.appendChild(script)
  })
}

/** The server's structured refusal for a verified Google account with no AFOS workspace. */
export type NoWorkspace = { email: string; message: string }

function parseRefusal(err: unknown): { noWorkspace?: NoWorkspace; message: string } {
  const fallback = 'Google sign-in failed. Try again or use an email code.'
  if (!(err instanceof Error) || !err.message) return { message: fallback }
  try {
    const parsed = JSON.parse(err.message) as { detail?: unknown }
    const detail = parsed.detail
    if (detail && typeof detail === 'object' && (detail as { code?: string }).code === 'no_workspace') {
      const d = detail as { email?: string; message?: string }
      return {
        noWorkspace: { email: String(d.email || ''), message: String(d.message || '') },
        message: String(d.message || ''),
      }
    }
    return { message: typeof detail === 'string' ? detail : fallback }
  } catch {
    return { message: err.message.length < 160 ? err.message : fallback }
  }
}

type Props = {
  onNoWorkspace?: (info: NoWorkspace) => void
}

/**
 * Official Google Identity Services button (rendered by Google's own script, popup mode).
 * The ID token goes to the server, which verifies it and decides access. Renders nothing
 * when no Google client is configured, so email codes are the only method shown.
 */
export function GoogleSignIn({ onNoWorkspace }: Props) {
  const { googleClientId, signInWithGoogleCredential } = useAuth()
  const hostRef = useRef<HTMLDivElement>(null)
  const noWorkspaceRef = useRef(onNoWorkspace)
  useEffect(() => {
    noWorkspaceRef.current = onNoWorkspace
  }, [onNoWorkspace])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function mount() {
      if (!googleClientId || !hostRef.current) return
      try {
        await loadGis()
        if (cancelled || !hostRef.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          callback: async (response: { credential?: string }) => {
            if (!response.credential) return
            setBusy(true)
            setError(null)
            try {
              await signInWithGoogleCredential(response.credential)
            } catch (err) {
              const refusal = parseRefusal(err)
              if (refusal.noWorkspace && noWorkspaceRef.current) {
                window.google?.accounts.id.disableAutoSelect()
                noWorkspaceRef.current(refusal.noWorkspace)
              } else {
                setError(refusal.message)
              }
            } finally {
              setBusy(false)
            }
          },
        })
        hostRef.current.innerHTML = ''
        const width = Math.min(400, Math.max(240, Math.floor(hostRef.current.clientWidth || 320)))
        window.google.accounts.id.renderButton(hostRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          logo_alignment: 'left',
          width,
        })
        setReady(true)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Google sign-in could not load')
      }
    }
    void mount()
    return () => {
      cancelled = true
    }
  }, [googleClientId, signInWithGoogleCredential])

  if (!googleClientId) return null

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative flex min-h-11 w-full justify-center">
        {!ready && !error && (
          <div className="absolute inset-0 animate-pulse rounded-full bg-white/10" aria-hidden />
        )}
        <div ref={hostRef} className="flex w-full justify-center" data-testid="gis-button" />
      </div>
      {busy && (
        <p role="status" className="text-sm text-[var(--color-mist)]">
          Verifying your Google account…
        </p>
      )}
      {error && (
        <p role="alert" className="text-center text-sm text-[var(--color-rose)]">
          {error}
        </p>
      )}
    </div>
  )
}
